import { useState } from "react";
import {
  Box,
  TransparentButton,
  SecondaryButton,
  TertiaryButton,
} from "flicket-ui";
import { ContentModal, Icon } from "~components";
import {
  ExtendedFile,
  LandingPage,
  VenueAddressFragment,
  VenueInfoFragment,
} from "~graphql/sdk";
import BaseHeader from "./common.BaseHeader";
import { getEventVenueLocation } from "~lib/helpers/getEventVenueLocation";
import { useOrganization } from "~hooks";
import { formatEventDateRange } from "~lib/helpers/formatDate";
import { Info, Question, Wheelchair } from "@phosphor-icons/react";

type EventHeaderProps = {
  goBack: () => void;
  title: string;
  startDate: string;
  endDate?: string;
  type: "event" | "membership";
  image?: ExtendedFile;
  coordinates?: string;
  accessibility?: string;
  description?: string;
  supportInfo?: string;
  venue: VenueInfoFragment & VenueAddressFragment;
  location?: string;
};

export default function EventHeader({
  title,
  startDate,
  endDate,
  location: maybeLocation,
  venue,
  coordinates,
  goBack,
  type,
  accessibility,
  description,
  image,
  supportInfo,
}: EventHeaderProps) {
  const { organization, hasFeature, t } = useOrganization();
  const [isOpen, setIsOpen] = useState(false);
  const [isOpenInfo, setIsOpenInfo] = useState(false);
  const [isOpenSupport, setIsOpenSupport] = useState(false);

  const location = maybeLocation ?? getEventVenueLocation(venue);

  let backTxt = `Back to ${
    type === "membership"
      ? t("membership_name_plural").toLowerCase()
      : "schedule"
  }`;

  if (organization.landingPage === LandingPage.SingleEvent) {
    backTxt = `Back to tickets`;
  }

  const formattedDateRange = formatEventDateRange(startDate, endDate, {
    format: "basicLong",
    showStartTime: !hasFeature("HideEventTime"),
    timeZone: venue?.timezone,
    locale: venue?.locale,
  });

  const subTitle = (
    <>
      {formattedDateRange}
      &nbsp;
      <Box as="span" d={{ _: "none", md: "block" }}>
        - {location}
      </Box>
    </>
  );

  return (
    <>
      <BaseHeader
        title={title}
        subTitle={subTitle}
        backTxt={backTxt}
        onClickBack={goBack}
        mobileMenuItems={
          <>
            {description && (
              <TransparentButton
                onClick={() => setIsOpenInfo(true)}
                alignSelf="flex-start"
              >
                <Info />
                Event info
              </TransparentButton>
            )}
            {accessibility && (
              <TransparentButton
                onClick={() => setIsOpen(true)}
                alignSelf="flex-start"
              >
                <Wheelchair />
                Accessibility
              </TransparentButton>
            )}
            {supportInfo && (
              <TransparentButton
                onClick={() => setIsOpenSupport(true)}
                alignSelf="flex-start"
              >
                <Question />
                Support info
              </TransparentButton>
            )}
          </>
        }
        desktopMenuItems={
          <>
            {accessibility && (
              <TertiaryButton
                onClick={() => setIsOpen(true)}
                size="md"
                iconSize="md"
                fontWeight="regular"
              >
                <Icon icon="accessibility" />
                Accessibility
              </TertiaryButton>
            )}
            {description && (
              <SecondaryButton
                onClick={() => setIsOpenInfo(true)}
                size="md"
                iconSize="md"
                fontWeight="regular"
              >
                <Info weight="fill" />
                {organization.landingPage === LandingPage.SingleEvent
                  ? "Ticket"
                  : type === "event"
                    ? "Event"
                    : t("membership_name")}{" "}
                Info
              </SecondaryButton>
            )}
            {supportInfo && (
              <SecondaryButton
                onClick={() => setIsOpenSupport(true)}
                size="md"
                iconSize="md"
                fontWeight="regular"
              >
                <Question weight="fill" />
                Support
              </SecondaryButton>
            )}
          </>
        }
      >
        <>
          <ContentModal
            isOpen={isOpen}
            setIsOpen={() => setIsOpen(false)}
            title="Accessibility"
            richText={accessibility}
          />
          {description && (
            <ContentModal
              isOpen={isOpenInfo}
              setIsOpen={() => setIsOpenInfo(false)}
              title={title}
              image={image}
              richText={description}
              subtitle={location}
              location={location}
              coordinates={coordinates}
              date={formattedDateRange}
            />
          )}
          {supportInfo && (
            <ContentModal
              isOpen={isOpenSupport}
              setIsOpen={() => setIsOpenSupport(false)}
              title="Support information"
              richText={supportInfo}
            />
          )}
        </>
      </BaseHeader>
    </>
  );
}
